import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bot, Sparkles, Loader2, ArrowRight, Clock } from 'lucide-react';
import axios from 'axios';
import Header from '../Header';
import { Button } from '../ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarkdownMessage = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
      ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
      ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
      li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
      strong: ({ node, ...props }) => <strong className="font-semibold text-white" {...props} />,
      h1: ({ node, ...props }) => <h1 className="text-base font-bold text-white mt-2 mb-1" {...props} />,
      h2: ({ node, ...props }) => <h2 className="text-sm font-bold text-white mt-2 mb-1" {...props} />,
      h3: ({ node, ...props }) => <h3 className="text-sm font-semibold text-white mt-2 mb-1" {...props} />,
      code: ({ node, inline, ...props }) => inline
        ? <code className="bg-black/40 text-indigo-200 rounded px-1 py-0.5 text-[12px]" {...props} />
        : <code className="block bg-black/50 text-indigo-100 rounded-lg p-3 my-2 text-[12px] overflow-x-auto" {...props} />,
      a: ({ node, ...props }) => <a className="text-indigo-300 underline" target="_blank" rel="noopener noreferrer" {...props} />,
      blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-indigo-500/50 pl-3 italic text-gray-300 my-2" {...props} />,
    }}
  >
    {text}
  </ReactMarkdown>
);

const SharedBimIAPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bimia/shared/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="min-h-screen bg-black" data-testid="bimia-shared-page">
      <Header onLoginClick={() => {}} />

      <section className="relative pt-28 pb-8 bg-gradient-to-b from-indigo-950/40 via-zinc-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-indigo-600 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-600/15 border border-indigo-500/30 text-indigo-300 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles size={14} /> Conversa compartilhada
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">AJ | BIM <span className="text-indigo-400">IA</span></h1>
          <p className="text-gray-400 text-sm max-w-xl mx-auto">
            Uma conversa com o tutor de IA de Revit da AJ | BIM.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 py-8 max-w-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400" data-testid="bimia-shared-loading">
            <Loader2 className="animate-spin mr-2 text-indigo-400" size={20} /> Carregando conversa...
          </div>
        ) : error || !data ? (
          <div className="text-center py-16" data-testid="bimia-shared-error">
            <Bot className="mx-auto text-zinc-600 mb-4" size={48} />
            <h2 className="text-white font-semibold text-lg mb-2">Conversa não encontrada</h2>
            <p className="text-gray-400 text-sm mb-6">Este link pode ter expirado ou não está mais disponível.</p>
            <Link to="/bim-ia">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bimia-shared-cta-error">
                Conhecer o AJ | BIM IA <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 md:p-6" data-testid="bimia-shared-chat">
              <div className="flex items-center gap-2 pb-4 mb-4 border-b border-zinc-800">
                <div className="bg-indigo-600/20 border border-indigo-500/40 rounded-lg p-2">
                  <Bot size={18} className="text-indigo-300" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm" data-testid="bimia-shared-title">{data.title}</div>
                  <div className="text-gray-500 text-xs flex items-center gap-1"><Clock size={11} /> Suporte técnico de Revit por IA</div>
                </div>
              </div>
              <div className="space-y-4">
                {data.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-indigo-600 text-white whitespace-pre-wrap' : 'bg-zinc-800 text-gray-100 border border-zinc-700'}`} data-testid={`bimia-shared-msg-${m.role}`}>
                      {m.role === 'assistant' ? <MarkdownMessage text={m.content || ''} /> : m.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-br from-indigo-950/60 to-zinc-900 border border-indigo-500/30 rounded-2xl p-6 text-center" data-testid="bimia-shared-cta">
              <h3 className="text-white font-bold text-lg mb-1">Tire suas próprias dúvidas de Revit</h3>
              <p className="text-gray-400 text-sm mb-4 max-w-lg mx-auto">
                O AJ | BIM IA é seu tutor de Revit disponível 24/7. Comece com 1 pergunta grátis.
              </p>
              <Link to="/bim-ia">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bimia-shared-cta-button">
                  <Sparkles size={16} className="mr-2" /> Usar o AJ | BIM IA
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedBimIAPage;
